import Vue from 'vue'
import { Component, Inject, Model, Prop, Watch } from 'vue-property-decorator'
import * as api from "../api";
import { tree, cc } from "../utils";
import { Item, InputRow, ListColumn, Node } from "../models";
import FormRows from "../parts/FormRows";
import Tab from "../parts/Tab"
import format from '../format'
import App from '../App'
import { shuffle, sleep } from '../utils'
import SelectNode, { SelectNodeResult } from '../parts/SelectNode'
import { remoteOp } from "../helper";
import { toast } from "../ui";

declare const Sortable: any

const confirmMsg = '排序已修改, 是否放弃保存?'

interface UnsetState {
  kind: 'unset';
}

interface ErrorState {
  kind: 'error';
  message: string;
}

interface DoneState {
  kind: 'done';
  items: Item[];
  columns: ListColumn[];
  selected: number[];
  sorting: {
    indices: number[];
    modified: boolean;
    key: string | null;
    ast: boolean;
  } | null;
}

type State = UnsetState | ErrorState | DoneState

interface ICommand {
  label: string;
  mode: 'normal' | 'selection' | 'item'
  execute(vm: PortalItemList, item?: Item): void;
}

@Component({
  name: 'PortalItemList',
  components: { Tab },
  filters: {
    datetime(value: number) {
      let o = new Date(value * 1000)
      return format(o, 'Y-m-d H:i')
    }
  }
})
export default class PortalItemList extends Vue {
  @Prop() root: Node

  nodeId = 0

  node: Node | null = null

  state: State = {kind: 'unset'}

  loading = false

  busy = false

  private _sortable: any

  private _token: cc.Token

  formSelectNode = SelectNode

  commands: ICommand[] = []

  executeCommand(command: ICommand, item?: Item) {
    if (this.state.kind === 'done') {
      command.execute(this, item)
    }
  }

  get status() {
    return this.state.kind
  }

  get modified() {
    return this.state.kind === 'done'
        && this.state.sorting
        && this.state.sorting.modified
  }

  get items(): ReadonlyArray<Item> {
    if (this.state.kind === 'done') {
      let state = this.state
      if (state.sorting) {
        return state.sorting.indices.map(i => state.items[i])
      }
      else
        return state.items
    }
    else
      throw new Error('runtime error.')
  }

  get sorting() {
    if (this.state.kind === 'done') {
      return this.state.sorting
    }
    else
      throw new Error('runtime error.')
  }

  get selectionState(): 'none' | 'all' | 'some' {
    if (this.state.kind === 'done') {
      let state = this.state
      let len = state.selected.length
      return len === 0 ? 'none' : (len === state.items.length ? 'all' : 'some')
    }
    else
      throw new Error('runtime error.')
  }

  get itemDefinition() {
    let app = this.$root.$children[0] as App
    if (this.node) {
      let type = app.itemDefinitions.map[this.node.type]
      if (type) {
        return app.itemDefinitions.types[type]
      }
    }
    return null
  }

  get callbacks() {
    let prepend = (item: Item) => {
      if (this.state.kind === 'done') {
        this.state.items.unshift(item)
      }
    }
    let update = (newValue: Item) => {
      if (this.state.kind === 'done') {
        let old = this.state.items.find(item => item.id === newValue.id)
        if (old) {
          for (let k in newValue) {
            if ((old as Object).hasOwnProperty(k)) {
              old[k] = newValue[k]
            }
          }
        }
      }
    }
    return { prepend, update }
  }

  getItemLink(id: number) {
    let router = this.$router
    let route = this.$route
    let location = {
      name: 'portal-item-update',
      params: {itemId: id.toString()}
    }
    return router.resolve(location, route).href
  }

  created() {
    this._token = {}
    this.reset()
  }

  @Watch('$route')
  $routeChanged() {
    if (this.nodeId.toString() !== this.$route.params.nodeId) {
      this.reset().then(() => {
        let el = this.$el.querySelector('div.body-wrapper')
        if (el) {
          el.scrollTop = 0
        }
      })
    }
  }

  beforeDestroy() {
    if (this._token.cancel) {
      this._token.cancel()
    }
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = undefined
    }
  }

  async reset() {
    if (this._token.cancel) {
      this._token.cancel()
    }
    let nodeId  = parseInt(this.$route.params.nodeId)
    this.nodeId = nodeId
    let node = tree.find(this.root, this.nodeId)
    if (!node) {
      this.state = {kind: 'error', message: 'Invalid node id.'}
      return
    }
    this.node = node
    let itemDefinition = this.itemDefinition
    if (!itemDefinition) {
      this.state = {kind: 'error', message: 'Invalid node config.'}
      return
    }
    await sleep(0)
    this.loading = true
    try {
      let portalKey = this.$route.params.portalKey
      let fields = itemDefinition.columns.map(c => c.key)
      fields = fields.concat(['id', 'type', 'update_time'])
      let result = await cc.wrap(api.item.list(nodeId, fields), this._token)
      if (result !== cc.canceled) {
        this.state = {
          kind: 'done',
          items: result,
          columns: itemDefinition.columns,
          selected: [],
          sorting: null
        }
        this.loading = false
      }
    }
    catch (e) {
      this.state = {kind: 'error', message: (e as Error).message}
      this.loading = false
    }
  }

  changeSelection() {
    if (this.state.kind === 'done') {
      let state = this.state
      let selected: number[] = []
      if (state.selected.length === 0) {
        for (let item of this.items)
          selected.push(item.id)
      }
      state.selected = selected
    }
  }

  toCreate() {
    this.$router.replace({ name: 'portal-item-create' })
  }

  async remove(event: MouseEvent, id: number) {
    if (this.state.kind !== 'done')
      return
    let state = this.state
    let app = this.$root.$children[0] as App
    let el = event.srcElement!.parentElement!.parentElement!
    if (await app.exposeAndConfirm(el, '删除操作不可恢复, 是否继续?')) {
      remoteOp(this, '删除', '项目', async () => {
        await api.item.remove(id)
        let index = state.items.findIndex(n=>n.id == id)
        if (index !== -1)
          state.items.splice(index, 1)
        index = state.selected.indexOf(id)
        if (index !== -1)
          state.selected.splice(index, 1)
      }, 880)
    }
  }

  removeSelected() {
    if (!(this.state.kind === 'done' && this.state.selected.length))
      return
    let state = this.state
    remoteOp(this, '删除', '项目', async () => {
      await api.item.removeArray(state.selected)
      let cc = state.items.length
      for (let i=0; i<state.items.length; i++) {
        if (state.selected.indexOf(state.items[i].id)!==-1) {
          state.items.splice(i, 1)
          i--;
          if (!--cc)
            break
        }
      }
      state.selected = []
    })
  }

  async toggle(item: Item, key: string) {
    let newValue = item[key] == 1 ? 0 : 1
    let app = this.$root.$children[0] as App
    try {
      await api.item.update(item.id, {[key]: newValue})
      item[key] = newValue
    }
    catch (e) {
      let detail = (e as Error).message
      toast('error', '项目设置失败', detail).close('manual')
    }
  }

  get dstFilter() {
    let app = this.$root.$children[0] as App
    if (this.node) {
      let type = app.itemDefinitions.map[this.node.type]
      if (type) {
        return (node: Node) => node.id !== this.nodeId && app.itemDefinitions.map[node.type] === type
      }
    }
    return (node: Node) => false
  }

  move(arg: SelectNodeResult) {
    if (!(this.state.kind === 'done' && this.state.selected.length))
      return
    let state = this.state
    remoteOp(this, '移动', '项目', async () => {
      let ids = state.selected
      await api.item.updateArray(ids.map(v => ({id: v, data: {node_id: arg.nodeId}})))
      if (arg.active) {
        this.$router.replace({
          name: 'portal-item-list',
          params: {
            portalKey: arg.portal,
            nodeId: arg.nodeId.toString(),
          }
        })
      }
      else {
        let cc = state.items.length
        for (let i=0; i<state.items.length; i++) {
          if (state.selected.indexOf(state.items[i].id)!==-1) {
            state.items.splice(i, 1)
            i--;
            if (!--cc)
              break
          }
        }
        state.selected = []
      }
    })
  }

  enterSorting() {
    if (this.state.kind !== 'done')
      return
    let state = this.state
    if (this.state.sorting)
      return
    if (state.selected.length) {
      state.selected = []
    }
    state.sorting = {
      indices: state.items.map((_, index) => index),
      modified: false,
      key: null,
      ast: false,
    }
    let el = this.$el.querySelector('div.wg-list')!
    this._sortable = Sortable.create(this.$el.querySelector('div.body')!, {
      animation: 120,
      onUpdate: evt => {
        if (state.sorting) {
          let s = state.sorting
          s.modified = true
          s.key = null
          s.indices.splice(evt.newIndex, 0, s.indices.splice(evt.oldIndex, 1)[0])
        }
      },
      onStart: evt => {
        el.classList.add('dragging')
      },
      onEnd: evt => {
        el.classList.remove('dragging')
      }
    })
  }

  leaveSotring() {
    if (this.state.kind !== 'done')
      return
    let state = this.state
    if (state.sorting) {
      this.$el.querySelector('div.item-list-content')!.classList.remove('list-sort')
      state.sorting = null
    }
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = undefined
    }
  }

  saveSorting() {
    if (this.state.kind !== 'done')
      return
    let state = this.state
    if (!state.sorting)
      return
    if (!state.sorting.modified) {
      if (!confirm('排序似乎未被修改, 是否继续?'))
        return
    }
    let indices = state.sorting.indices
    remoteOp(this, '保存', '排序', async () => {
      let list = indices.map((i, value) => {return {id: state.items[i].id, value} })
      await api.item.updateArray(list.map(v => ({ id: v.id, data: { sort: v.value } })))
      this.leaveSotring()
      let original = state.items
      let newList: Item[] = []
      for (let index of indices) {
        newList.push(original[index])
      }
      state.items = newList
    })
  }

  reverse() {
    if (this.state.kind !== 'done')
      return
    if (this.state.sorting) {
      let ss = this.state.sorting
      if (ss.key) {
        this.sortBy(ss.key, !ss.ast)
      }
      else {
        ss.indices.reverse()
      }
      ss.modified = true
    }
  }

  shuffle() {
    if (this.state.kind !== 'done')
      return
    let state = this.state
    if (state.sorting) {
      state.sorting.indices = shuffle(state.sorting.indices)
      state.sorting.key = null
      state.sorting.modified = true
    }
  }

  sortBy(key: string, ast: boolean) {
    if (this.state.kind !== 'done')
      return
    if (!this.state.sorting)
      return
    let ss = this.state.sorting
    let items = this.state.items
    let tostr = val => val === undefined || val === null ? '' : val.toString()
    if (ss.key !== key || ss.ast !== ast) {
      ss.key = key
      ss.ast = ast
      ss.modified = true
      let compare = new Intl.Collator('en', { numeric: true }).compare
      ss.indices.sort((a, b) => {
        let aValue = items[a][key]
        let bValue = items[b][key]
        let result: number
        if (typeof aValue === 'number' && typeof bValue === 'number')
          result = aValue - bValue
        else if (typeof aValue === 'boolean' && typeof bValue === 'boolean')
          result = (aValue ? 1 : 0) - (bValue ? 1 : 0)
        else
          result = compare(tostr(aValue), tostr(bValue))
        return ast ? result : -result;
      })
    }
  }
}


